import React from 'react';
import cn from 'classnames';
import { GQL, App } from '@gin-betting/core';
import type { Betslip } from '@gin-betting/betslip';

import { BettingWidgets, LIVE_SPORTEVENT_STATUSES } from 'app-constants';
import PromoHeader from '../PromoHeader';
import PromoFooter from '../PromoFooter';
import PromoMatchBody from '../PromoMatchBody';
import { usePromoItemContainer, useWidgetSettings } from 'hooks';
import { checkIsSportEventViewable, checkIsMatchTrackerDisabled } from 'utils';
import './promoItem.scss';

interface Props {
    matchId: string;
    type?: GQL.BannerType;
    AsyncOddButton: Betslip.OddButtonComponent;
    favoriteButton: JSX.Element;
    header?: JSX.Element | null;
    footer?: JSX.Element | null;
    isColorful?: boolean;
}

const PromoItem: React.FC<Props> = props => {
    const {
        match,
        competitors,
        tournament,
        market,
        hasValidStream,
        hasValidBetradarId,
        route,
    } = usePromoItemContainer(props);

    const { disableMatchTracker } = useWidgetSettings({
        widget: BettingWidgets.STREAM,
    });

    const { type, favoriteButton, AsyncOddButton, isColorful } = props;

    if (!match) return null;

    const {
        id,
        marketsCount,
        startTime,
        sportId,
        slug,
        status,
        liveCoverage,
    } = match;

    const linkToMatch = `${route}${App.Routes.MATCH}${slug}`;

    const isLive = type === GQL.BannerType.betting_live_of_the_day;
    const isMatch = type === GQL.BannerType.betting_match_of_the_day;
    const isTop = type === GQL.BannerType.betting_sport_event;

    const isStatusLive = LIVE_SPORTEVENT_STATUSES.includes(status);

    const isWatchable =
        (isLive || isStatusLive) &&
        checkIsSportEventViewable({
            hasValidBetradarId,
            hasValidStream,
            isMatchTrackerDisabled: checkIsMatchTrackerDisabled(
                disableMatchTracker,
                sportId
            ),
        });

    let liveLabelVisibility: 'visible' | 'faded' | undefined;

    if (isStatusLive) {
        liveLabelVisibility = 'visible';
    } else if (liveCoverage) {
        liveLabelVisibility = 'faded';
    }

    let { header, footer } = props;

    if (typeof header === 'undefined' && type) {
        header = (
            <PromoHeader
                sportId={sportId}
                type={type}
                isColorful={isColorful}
            />
        );
    }

    if (typeof footer === 'undefined') {
        footer = (
            <PromoFooter
                linkToMatch={linkToMatch}
                marketsCount={marketsCount}
                isWatchable={isWatchable}
                favoriteButton={favoriteButton}
            />
        );
    }

    return (
        <div
            className="__app-PromoItem-container"
            styleName={cn('container ', {
                'is-live': isLive,
                'is-match': isTop || isMatch,
            })}
        >
            {header}

            <PromoMatchBody
                route={route}
                matchId={id}
                linkToMatch={linkToMatch}
                isLive={isLive || isStatusLive}
                competitors={competitors}
                oddIds={market?.odds}
                tournament={tournament}
                dateAndTime={startTime}
                marketName={market?.name ?? ''}
                OddButton={AsyncOddButton}
                sportId={sportId}
                liveLabelVisibility={liveLabelVisibility}
            />

            {footer}
        </div>
    );
};

export default PromoItem;
